// --- BEFORE/AFTER SLIDER + LOGO CAROUSEL INIT ---
// Inicializado desde render.js tras cada renderPage.
import { registerPostRender } from '../core/render.js';
import { animate, remove } from '../vendor/anime.esm.js';
import { HOVER_SPRING, IS_TOUCH } from './motion-tokens.js';
import { store } from '../core/store.js';

// ── BEFORE/AFTER ──────────────────────────────────────────
function initBeforeAfterSlider(container) {
    const uid = container.id;
    const beforeEl = container.querySelector('.ba-before');
    const line = container.querySelector('.ba-slider-line');
    const handle = container.querySelector('.ba-handle');
    if (!beforeEl || !line) return;

    let dragging = false;

    const setPos = (pct) => {
        const clamped = Math.max(2, Math.min(98, pct));
        beforeEl.style.clipPath = `inset(0 ${100 - clamped}% 0 0)`;
        line.style.left = `${clamped}%`;
    };

    // Start at 50%
    setPos(50);

    const getPct = (e) => {
        const rect = container.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        return ((clientX - rect.left) / rect.width) * 100;
    };

    const onStart = (e) => { dragging = true; e.preventDefault(); };
    const onMove = (e) => { if (dragging) setPos(getPct(e)); };
    const onEnd = () => { dragging = false; };

    const controller = new AbortController();
    const signal = controller.signal;

    handle.addEventListener('mousedown', onStart);
    handle.addEventListener('touchstart', onStart, { passive: false });
    window.addEventListener('mousemove', onMove, { signal });
    window.addEventListener('touchmove', onMove, { passive: false, signal });
    window.addEventListener('mouseup', onEnd, { signal });
    window.addEventListener('touchend', onEnd, { signal });

    // Cleanup para re-renders
    container._baCleanup = () => {
        controller.abort();
    };
}

// ── LOGO CAROUSEL ────────────────────────────────────────
function initLogoCarousel(outer) {
    const track = outer.querySelector('.logos-track');
    if (!track) return;

    // Ajustar velocidad según cantidad de items (la mitad son duplicados)
    const itemCount = track.querySelectorAll('.logo-item').length / 2;
    const duration = Math.max(20, itemCount * 4); // Ajuste dinámico de velocidad
    track.style.animationDuration = `${duration}s`;

    // Pausar en hover
    outer.addEventListener('mouseenter', () => track.style.animationPlayState = 'paused');
    outer.addEventListener('mouseleave', () => track.style.animationPlayState = 'running');
}

// ── TILT 3D CARD BINDING ─────────────────────────────────
const bindTilt3D = (el) => {
    if (store.get('editorMode')) return;
    
    el.style.transformStyle = 'preserve-3d';
    el.style.transition = 'transform 0.08s ease-out';
    
    const onMouseMove = (e) => {
        if (el._hoverAnim) {
            el._hoverAnim.pause();
            el._hoverAnim = null;
        }
        const rect = el.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 a 0.5
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        el.style.transform = `perspective(1000px) rotateY(${x * 12}deg) rotateX(${-y * 12}deg) translateY(-8px) scale(1.02)`;
    };
    
    const onMouseLeave = () => {
        el._hoverAnim = animate(el, {
            rotateY: 0,
            rotateX: 0,
            translateY: 0,
            scale: 1,
            duration: 600,
            ease: HOVER_SPRING.card
        });
    };
    
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseleave', onMouseLeave);
    window._hoverListeners.push({el, type: 'mousemove', fn: onMouseMove});
    window._hoverListeners.push({el, type: 'mouseleave', fn: onMouseLeave});
};

// ── MAGNETIC BUTTON BINDING ──────────────────────────────
const bindMagnetic = (el) => {
    if (store.get('editorMode')) return;
    
    let btnX = 0;
    let btnY = 0;
    let isHovered = false;
    
    const onMouseMove = (e) => {
        if (!isHovered) return;
        
        const dx = e.clientX - btnX;
        const dy = e.clientY - btnY;
        
        animate(el, {
            translateX: dx * 0.38,
            translateY: dy * 0.38,
            scale: 1.06,
            duration: 150,
            ease: 'easeOutQuad'
        });
    };
    
    const onMouseEnter = () => {
        isHovered = true;
        const rect = el.getBoundingClientRect();
        // Evitamos recalcular el centro dinámicamente al moverse para prevenir la vibración
        btnX = rect.left + rect.width / 2;
        btnY = rect.top + rect.height / 2;
        
        window.addEventListener('mousemove', onMouseMove);
    };
    
    const onMouseLeave = () => {
        isHovered = false;
        window.removeEventListener('mousemove', onMouseMove);
        
        animate(el, {
            translateX: 0,
            translateY: 0,
            scale: 1,
            duration: 700,
            ease: HOVER_SPRING.button
        });
    };
    
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
    window._hoverListeners.push({el, type: 'mouseenter', fn: onMouseEnter});
    window._hoverListeners.push({el, type: 'mouseleave', fn: onMouseLeave});
};

// ── SCROLL VELOCITY MOTION BLUR ──────────────────────────
let lastScrollY = window.scrollY;
let lastTime = performance.now();
let blurTimeout = null;
let activeBlurElements = [];

function initScrollVelocityBlur(root) {
    if (IS_TOUCH) return;
    if (store.get('editorMode')) return;
    
    activeBlurElements = Array.from(root.querySelectorAll('.unsplash-target, .card, .ba-container'));
    
    const onScroll = () => {
        const currentScrollY = window.scrollY;
        const currentTime = performance.now();
        
        const deltaY = Math.abs(currentScrollY - lastScrollY);
        const deltaTime = currentTime - lastTime;
        
        const velocity = deltaY / (deltaTime || 1); // px / ms
        
        lastScrollY = currentScrollY;
        lastTime = currentTime;
        
        if (velocity > 0.8) {
            const blurVal = Math.min(2.5, velocity * 0.6);
            activeBlurElements.forEach(el => {
                el.style.filter = `blur(${blurVal}px)`;
                el.style.transition = 'filter 0.05s ease-out';
            });
            
            if (blurTimeout) clearTimeout(blurTimeout);
            
            blurTimeout = setTimeout(() => {
                activeBlurElements.forEach(el => {
                    el.style.filter = 'blur(0px)';
                    el.style.transition = 'filter 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
                });
            }, 100);
        }
    };
    
    window.addEventListener('scroll', onScroll, { passive: true });
    window._hoverListeners.push({el: window, type: 'scroll', fn: onScroll});
}

// ── HERO PARALLAX DEPTH LAYERS ──────────────────────────
const bindHeroParallax = (hero) => {
    if (store.get('editorMode')) return;

    const wrapper = hero.querySelector('.hero-media-wrapper');
    const textSide = hero.querySelector('.hero-text-side');
    const ghost = hero.querySelector('.ghost-text');

    const onMouseMove = (e) => {
        const rect = hero.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 a 0.5
        const y = (e.clientY - rect.top) / rect.height - 0.5;

        if (wrapper) {
            wrapper.style.transform = `perspective(1000px) translate3d(${x * 35}px, ${y * 35}px, 20px) rotateY(${x * 4}deg) rotateX(${-y * 4}deg)`;
        }
        if (textSide) {
            textSide.style.transform = `translate3d(${x * -20}px, ${y * -20}px, 0)`;
        }
        if (ghost) {
            // Por variables, NUNCA por `transform`: el centrado (translate(-50%,-50%))
            // vive en site.css y escribir transform aquí lo borraba. Amplitud 60→16: es
            // una marca de agua a opacity .04, no un elemento protagonista — con 60px
            // arrastraba la mirada y se comía la sección.
            ghost.style.setProperty('--ghost-x', (x * 16).toFixed(1) + 'px');
            ghost.style.setProperty('--ghost-y', (y * 16).toFixed(1) + 'px');
        }
    };

    const onMouseLeave = () => {
        if (wrapper) {
            animate(wrapper, {
                translateX: 0,
                translateY: 0,
                rotateY: 0,
                rotateX: 0,
                duration: 900,
                ease: 'easeOutElastic(1, 0.8)'
            });
        }
        if (textSide) {
            animate(textSide, {
                translateX: 0,
                translateY: 0,
                duration: 900,
                ease: 'easeOutElastic(1, 0.8)'
            });
        }
        if (ghost) {
            // Vuelta al centro por las MISMAS variables (animar translateX/Y volvería a
            // escribir `transform` y a romper el centrado). Sin elástico: un rebote en la
            // marca de agua es justo el protagonismo que no queremos.
            const desde = {
                x: parseFloat(ghost.style.getPropertyValue('--ghost-x')) || 0,
                y: parseFloat(ghost.style.getPropertyValue('--ghost-y')) || 0,
            };
            animate(desde, {
                x: 0,
                y: 0,
                duration: 600,
                ease: 'outCubic',
                onUpdate: () => {
                    ghost.style.setProperty('--ghost-x', desde.x.toFixed(1) + 'px');
                    ghost.style.setProperty('--ghost-y', desde.y.toFixed(1) + 'px');
                },
            });
        }
    };

    hero.addEventListener('mousemove', onMouseMove);
    hero.addEventListener('mouseleave', onMouseLeave);
    window._hoverListeners.push({el: hero, type: 'mousemove', fn: onMouseMove});
    window._hoverListeners.push({el: hero, type: 'mouseleave', fn: onMouseLeave});
};

// ── HOVER SPRINGS DIFERENCIADOS ──────────────────────────
function initHoverSprings() {
    if (IS_TOUCH) return; // Móviles no tienen hover real

    const root = document.getElementById('resultado');
    if (!root) return;

    // Limpieza de listeners previos (para re-renders del Builder)
    if (window._hoverListeners) {
        window._hoverListeners.forEach(({el, type, fn}) => {
            try { el.removeEventListener(type, fn); } catch(e) {}
        });
    }
    window._hoverListeners = [];

    // 0. Hero Parallax Depth Layers
    root.querySelectorAll('.hero-section').forEach(bindHeroParallax);

    // 1. Tarjetas con Tilt 3D (Features, Plan, Team, Card general)
    root.querySelectorAll('.card, .feature-card, .team-card, .plan-card').forEach(bindTilt3D);

    // 2. Magnetic Buttons (CTA Buttons)
    root.querySelectorAll('.btn-action').forEach(bindMagnetic);

    // 3. Cinematic Scroll-Velocity Blur
    initScrollVelocityBlur(root);
}

// ── PUNTO DE ENTRADA ─────────────────────────────────────
// ── BOTÓN DEL BLOQUE `imagen`: RE-ACOTAR TRAS CADA RENDER ─────────────────────
// El acotado a los bordes se hacía SOLO mientras se arrastraba, y eso deja fuera todo lo que
// cambia el TAMAÑO del botón después de haberlo colocado: alargar su texto con la IA, editarlo
// a mano, o traducir el sitio a un idioma más largo. Caso real: se coloca el botón a la
// izquierda, se le alarga el texto, y el botón desborda la foto por los dos lados Y el asa de
// arrastre —que vive fuera de su caja— queda fuera de pantalla: ya no hay forma de recolocarlo.
//
// Por eso esto corre en CADA render y en las DOS superficies (el editor y el sitio publicado:
// una traducción larga desborda igual en el publicado, donde ni siquiera hay asa).
// Solo se toca el INLINE que pinta la posición, nunca los props: la intención del cliente se
// respeta, y si luego acorta el texto el botón vuelve solo a donde lo había dejado.
function ajustarCtaImagen(wrap) {
    const marco = wrap.closest('.imagen-marco');
    if (!marco) return;
    const c = marco.getBoundingClientRect();
    const e = wrap.getBoundingClientRect();
    if (!c.width || !c.height || !e.width || !e.height) return;

    const leer = (nombre, porDefecto) => {
        const v = parseFloat(getComputedStyle(wrap).getPropertyValue(nombre));
        return isFinite(v) ? v : porDefecto;
    };
    // Media anchura/altura REALES del botón, en % del marco: es lo que hay que dejar libre a
    // cada lado para que no se salga (el mismo criterio que usa el arrastre).
    const mx = (e.width / 2 / c.width) * 100;
    const my = (e.height / 2 / c.height) * 100;
    const x = Math.max(Math.min(50, mx + 1), Math.min(Math.max(50, 100 - mx - 1), leer('--pos-x', 50)));
    const y = Math.max(Math.min(50, my + 1), Math.min(Math.max(50, 100 - my - 1), leer('--pos-y', 88)));
    wrap.style.setProperty('--pos-x', x + '%');
    wrap.style.setProperty('--pos-y', y + '%');

    // …y RE-ACOTAR cuando el botón cambie de tamaño, no solo en el render. Es lo que faltaba en
    // el caso reportado: la burbuja de IA inline ("alargar el texto") reescribe el DOM SIN pasar
    // por `renderPage`, así que ningún render llegaba a recolocarlo. Con el botón colocado a la
    // izquierda y el texto ya largo, se extendía fuera de la foto por la izquierda —invisible— y
    // dejaba su × huérfana en mitad de la imagen. Un ResizeObserver lo cubre venga el cambio de
    // donde venga: la IA, teclear dentro del botón, la carga de una fuente o un resize.
    // Sin bucle: esto solo escribe POSICIÓN, que no altera el tamaño observado.
    if (!wrap._n3iRO && typeof ResizeObserver !== 'undefined') {
        wrap._n3iRO = new ResizeObserver(() => ajustarCtaImagen(wrap));
        wrap._n3iRO.observe(wrap);
        wrap._n3iRO.observe(marco);
    }
}

export function initInteractiveBlocks() {
    // Limpiar sliders anteriores antes de re-init
    document.querySelectorAll('.ba-container').forEach(c => {
        if (c._baCleanup) { c._baCleanup(); c._baCleanup = null; }
        initBeforeAfterSlider(c);
    });

    document.querySelectorAll('.logos-track-outer').forEach(initLogoCarousel);
    document.querySelectorAll('.imagen-cta-wrap').forEach(ajustarCtaImagen);
    initHoverSprings();
}

registerPostRender(initInteractiveBlocks);
